import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  Box,
  IconButton,
  Typography,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  TextIncrease as TextIncreaseIcon,
  TextDecrease as TextDecreaseIcon,
  RestartAlt as RestartAltIcon,
  Key as KeyIcon,
  ContentCopy as ContentCopyIcon,
  AccountBalance as AccountBalanceIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
} from '@mui/icons-material';
import { useFontSize } from '../contexts/FontSizeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePaperMode } from '../contexts/PaperModeContext';

const SettingsMenu = ({ collapsed = false, onExpand }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { scale, increase, decrease, reset, minScale, maxScale } = useFontSize();
  const { user } = useAuth();
  const { paperMode } = usePaperMode();

  const handleClick = () => {
    // Collapsed rail has no room for the submenu — expand first, then open.
    if (collapsed) {
      setOpen(true);
      onExpand?.();
      return;
    }
    setOpen(!open);
  };

  const settingsItems = [
    { text: 'User Profile', icon: <SettingsIcon />, path: '/user-profile' },
    { text: 'Alert Config', icon: <KeyIcon />, path: '/api-tokens' },
    { text: 'Copy Trading', icon: <ContentCopyIcon />, path: '/copy-trading' },
    ...(paperMode ? [] : [{ text: 'Accounts', icon: <AccountBalanceIcon />, path: '/accounts' }]),
    ...(user?.isAdmin ? [{ text: 'User Approvals', icon: <AdminPanelSettingsIcon />, path: '/admin/users' }] : []),
  ];

  // Check if any settings page is currently active
  const isSettingsActive = settingsItems.some(item => location.pathname === item.path);

  return (
    <>
      <Tooltip title={collapsed ? 'Settings' : ''} placement="right" arrow>
        <ListItemButton
          onClick={handleClick}
          selected={isSettingsActive}
          sx={collapsed ? { justifyContent: 'center', px: 0.5 } : { px: 1 }}
        >
          <ListItemIcon sx={collapsed ? { minWidth: 0, justifyContent: 'center' } : { minWidth: 36 }}>
            <SettingsIcon />
          </ListItemIcon>
          {!collapsed && <ListItemText primary="Settings" />}
          {!collapsed && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItemButton>
      </Tooltip>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {/* Font Size Controls */}
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Decrease font size">
              <span>
                <IconButton
                  size="small"
                  onClick={decrease}
                  disabled={scale <= minScale}
                >
                  <TextDecreaseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center' }}>
              {Math.round(scale * 100)}%
            </Typography>
            <Tooltip title="Increase font size">
              <span>
                <IconButton
                  size="small"
                  onClick={increase}
                  disabled={scale >= maxScale}
                >
                  <TextIncreaseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reset font size">
              <span>
                <IconButton
                  size="small"
                  onClick={reset}
                  disabled={scale === 1.0}
                >
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Divider sx={{ mb: 0.5 }} />
          {settingsItems.map((item) => (
            <ListItemButton
              key={item.text}
              component={Link}
              to={item.path}
              selected={location.pathname === item.path}
              sx={{ pl: 3, pr: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Box sx={{ fontSize: '1.2rem' }}>
                  {item.icon}
                </Box>
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          ))}
        </List>
      </Collapse>
    </>
  );
};

export default SettingsMenu;